import { useEffect, useState } from "react";

interface SoProducts {
    id?: number;
    soName: string;
    name: string;
    qtyOrdered: number;
    qtyShipped: number;
    qtyReceived: number;
    qtyNotShipped: number;
    etd: string | null;
    eta: string | null;
}

interface ShippingInfoColumn {
    name: string;
    visiable: boolean;
}

interface InfoTableProps {
    shippingInfoColumns: ShippingInfoColumn[];
    products: SoProducts[];
}

const CustomerInfoTable = (props: InfoTableProps) => {
    const [totalQty, setTotalQty] = useState(0);
    const [totalReceivedQty, setTotalReceivedQty] = useState(0);
    const [totalNotSippedQty, setTotalNotShippedQty] = useState(0);
    const [totalShippedQty, setTotalShippedQty] = useState(0);

    function isMobileScreen(): boolean {
        return typeof window !== "undefined" && window.innerWidth <= 768;
    }

    function truncateText(text: string | null, maxLength: number = 20): string {
        if (!text) return "";

        if (!isMobileScreen()) {
            return text; // no truncation on tablet/desktop
        }

        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    }

    function isDateOlderThanToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    }

    function isDateInTwoDays(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();
        const twoDaysFromNow = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        twoDaysFromNow.setHours(0, 0, 0, 0);
        twoDaysFromNow.setDate(today.getDate() + 2);

        return inputDate.getTime() === twoDaysFromNow.getTime();
    }

    function getNextStepColor(date: string | undefined) {
        if (!date) {
            return "#fff";
        }

        if (isDateOlderThanToday(date)) {
            return "#fa7f52ff";
        } else if (isDateInTwoDays(date)) {
            return "#e0f052ff";
        } else {
            return "#43bd38ff";
        }
    }

    function handleGoToNextStepAction(id: number) {
        window.open(
            `https://odoo-ps-psae-carandx.odoo.com/web#id=${id}&cids=3&menu_id=309&action=470&model=purchase.order&view_type=form`,
            "_blank",
            "noopener,noreferrer",
        );
    }

    useEffect(() => {
        let lTotalQty = 0;
        let lTotalReceivedQty = 0;
        let lTotalShippedQty = 0;
        let lTotalNotShippedQty = 0;

        for (let i = 0; i < props.products.length; i++) {
            const data = props.products[i];

            lTotalQty += data.qtyOrdered;
            lTotalReceivedQty += data.qtyReceived;
            lTotalShippedQty += data.qtyShipped;
            lTotalNotShippedQty += data.qtyNotShipped;
        }

        setTotalQty(lTotalQty);
        setTotalReceivedQty(lTotalReceivedQty);
        setTotalShippedQty(lTotalShippedQty);
        setTotalNotShippedQty(lTotalNotShippedQty);
    }, [props.products]);

    return (
        <table
            className="table table-hover mb-0"
            style={{
                borderRight: "1px solid #efefef",
                position: "sticky",
                left: 0,
            }}
        >
            {/* Table Header */}
            <thead
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#0d6efd",
                    color: "white",
                    zIndex: 10,
                }}
            >
                <tr>
                    {props.shippingInfoColumns.map((col) => {
                        if (col?.visiable) {
                            return (
                                <th
                                    key={col.name}
                                    className="fw-semibold text-uppercase"
                                    style={{
                                        fontSize: "clamp(0.2rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        color: "#6c757d",
                                        padding: "0.6rem",
                                        borderBottom: "2px solid #dee2e6",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {col.name}
                                </th>
                            );
                        }
                    })}
                </tr>
            </thead>
            {/* Table Body */}
            <tbody>
                {props.products.map((row, idx) => (
                    <tr
                        key={idx}
                        style={{
                            borderBottom: "1px solid #f0f0f0",
                            fontSize: 10,
                            height: 50,
                        }}
                    >
                        {props.shippingInfoColumns.map((column) => (
                            <>
                                {column.name == "SO Number" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                        }}
                                    >
                                        {truncateText(row.soName)}
                                    </td>
                                )}

                                {column.name == "Product" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                        }}
                                    >
                                        {truncateText(row.name)}
                                    </td>
                                )}

                                {column.name == "Ordered" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                            maxWidth: 15,
                                        }}
                                    >
                                        {row.qtyOrdered}
                                    </td>
                                )}

                                {column.name == "Shipped" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                            maxWidth: 15,
                                        }}
                                    >
                                        {row.qtyShipped}
                                    </td>
                                )}

                                {column.name == "Not Shipped" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                            maxWidth: 15,
                                        }}
                                    >
                                        {row.qtyNotShipped}
                                    </td>
                                )}

                                {column.name == "Delivered" && (
                                    <td
                                        style={{
                                            fontSize:
                                                "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                            fontWeight: "500",
                                            color: "#495057",
                                            maxWidth: 15,
                                        }}
                                    >
                                        {row.qtyReceived}
                                    </td>
                                )}
                            </>
                        ))}
                    </tr>
                ))}
            </tbody>
            <tfoot
                style={{
                    position: "sticky",
                    bottom: -1,
                    fontWeight: "600",
                    borderTop: "1px solid #ddd",
                    minHeight: 50,
                }}
            >
                <tr
                    style={{
                        borderBottom: "1px solid #f0f0f0",
                        fontSize: 12,
                        fontWeight: "bold",
                    }}
                >
                    {props.shippingInfoColumns.map((column) => (
                        <>
                            {column.name == "SO Number" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        textAlign: "end",
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    
                                </td>
                            )}

                            {column.name == "Product" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        textAlign: "end",
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    Total:
                                </td>
                            )}

                            {column.name == "Ordered" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    {totalQty}
                                </td>
                            )}

                            {column.name == "Not Shipped" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    {totalNotSippedQty}
                                </td>
                            )}

                            {column.name == "Shipped" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    {totalShippedQty}
                                </td>
                            )}

                            {column.name == "Delivered" && (
                                <td
                                    style={{
                                        fontSize: "clamp(0.5rem, 2vw, 0.7rem)", // Responsive between 0.5rem and 0.7rem
                                        fontWeight: "500",
                                        color: "#495057",
                                        minWidth: 30,
                                        backgroundColor: "#e0e0e0ff",
                                    }}
                                >
                                    {totalReceivedQty}
                                </td>
                            )}
                        </>
                    ))}
                </tr>
            </tfoot>
        </table>
    );
};

export default CustomerInfoTable;
